import { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import {
  TrendingUp,
  Calendar,
  Users,
  PieChart as PieIcon,
  Activity,
  Sparkles,
} from 'lucide-react';
import type { VisitDashboard, VisitSummary } from '../types';

interface VisitsAnalyticsChartsProps {
  visits: VisitSummary[];
  dashboard?: VisitDashboard;
  timeRangeVisits?: VisitSummary[];
}

const PIE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#6366F1', '#14B8A6'];

// Custom Glassmorphic Tooltip
function CustomChartTooltip({ active, payload, label }: any) {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-xl border border-slate-200/80 bg-white/95 p-3.5 shadow-xl backdrop-blur-md">
        <p className="text-xs font-bold text-slate-700 mb-1.5">{label}</p>
        <div className="space-y-1">
          {payload.map((entry: any, index: number) => (
            <div key={`item-${index}`} className="flex items-center justify-between gap-4 text-xs">
              <span className="flex items-center gap-1.5 font-medium" style={{ color: entry.color }}>
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
                {entry.name}:
              </span>
              <span className="font-bold tabular-nums text-slate-900">{entry.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

export function VisitsAnalyticsCharts({ visits, dashboard, timeRangeVisits = [] }: VisitsAnalyticsChartsProps) {
  const [chartMode, setChartMode] = useState<'weekly' | 'monthly' | 'reps' | 'purpose'>('weekly');

  // Source of all historical / range data
  const dataPool = timeRangeVisits.length > 0 ? timeRangeVisits : visits;

  // 1. Weekly / Daily trend (Last 7 to 14 days)
  const weeklyTrendData = useMemo(() => {
    const dayMap = new Map<string, { label: string; planned: number; completed: number; checkedIn: number; cancelled: number; total: number }>();
    
    // Generate dates for the last 7 days
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(now.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const dayName = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      dayMap.set(key, { label: dayName, planned: 0, completed: 0, checkedIn: 0, cancelled: 0, total: 0 });
    }

    // Populate data
    dataPool.forEach((v) => {
      if (!v.plannedAt) return;
      const dateKey = v.plannedAt.slice(0, 10);
      let entry = dayMap.get(dateKey);
      if (!entry) {
        // If outside the 7 day window, format label
        const d = new Date(v.plannedAt);
        const dayName = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        entry = { label: dayName, planned: 0, completed: 0, checkedIn: 0, cancelled: 0, total: 0 };
        dayMap.set(dateKey, entry);
      }
      entry.total += 1;
      if (v.status === 'completed') entry.completed += 1;
      else if (v.status === 'checked_in') entry.checkedIn += 1;
      else if (v.status === 'cancelled') entry.cancelled += 1;
      else entry.planned += 1;
    });

    // Return sorted by date
    return Array.from(dayMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-14)
      .map(([, val]) => val);
  }, [dataPool]);

  // 2. Monthly trend (Last 6 months)
  const monthlyTrendData = useMemo(() => {
    const monthMap = new Map<string, { month: string; planned: number; completed: number; converted: number; total: number; distanceKm: number }>();
    
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      monthMap.set(key, { month: monthLabel, planned: 0, completed: 0, converted: 0, total: 0, distanceKm: 0 });
    }

    dataPool.forEach((v) => {
      if (!v.plannedAt) return;
      const key = v.plannedAt.slice(0, 7);
      const entry = monthMap.get(key);
      if (entry) {
        entry.total += 1;
        if (v.status === 'completed') entry.completed += 1;
        else if (v.status === 'planned') entry.planned += 1;
        if (v.convertedLeadId || v.convertedFollowupId) entry.converted += 1;
        if (typeof v.distanceKm === 'number') entry.distanceKm += v.distanceKm;
      }
    });

    return Array.from(monthMap.values());
  }, [dataPool]);

  // 3. Purpose split data
  const purposeChartData = useMemo(() => {
    if (dashboard?.purposeSplit && dashboard.purposeSplit.length > 0) {
      return dashboard.purposeSplit.map((p) => ({
        name: p.label || 'Other',
        value: p.count,
      }));
    }
    const map = new Map<string, number>();
    dataPool.forEach((v) => {
      const p = v.purposeName || 'General Meeting';
      map.set(p, (map.get(p) || 0) + 1);
    });
    return Array.from(map.entries()).map(([name, value]) => ({ name, value }));
  }, [dashboard?.purposeSplit, dataPool]);

  // 4. Reps performance data
  const repChartData = useMemo(() => {
    if (dashboard?.repCounts && dashboard.repCounts.length > 0) {
      return dashboard.repCounts.map((r) => ({
        name: r.displayName || 'Rep',
        completed: r.completed,
        planned: r.planned,
        checkedIn: r.checkedIn,
        distanceKm: Number(r.routeDistanceKm.toFixed(1)),
      }));
    }
    const map = new Map<string, { name: string; completed: number; planned: number; checkedIn: number; distanceKm: number }>();
    dataPool.forEach((v) => {
      const rep = v.assignedToName || 'Unassigned';
      const item = map.get(rep) || { name: rep, completed: 0, planned: 0, checkedIn: 0, distanceKm: 0 };
      if (v.status === 'completed') item.completed += 1;
      else if (v.status === 'checked_in') item.checkedIn += 1;
      else item.planned += 1;
      if (typeof v.distanceKm === 'number') item.distanceKm += v.distanceKm;
      map.set(rep, item);
    });
    return Array.from(map.values());
  }, [dashboard?.repCounts, dataPool]);

  // Summary counts
  const totalCompleted = dataPool.filter((v) => v.status === 'completed').length;
  const completionRate = dataPool.length > 0 ? Math.round((totalCompleted / dataPool.length) * 100) : 0;
  const totalConversions = dataPool.filter((v) => v.convertedLeadId || v.convertedFollowupId).length;

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
      {/* Header & Mode Switcher */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-white shadow-sm shadow-indigo-200">
            <Activity className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
              Visit Velocity & Analytics
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700 border border-indigo-100">
                <Sparkles className="h-2.5 w-2.5" /> Live Insights
              </span>
            </h3>
            <p className="text-[12px] text-slate-500">Track field execution patterns, completion volume & team velocity</p>
          </div>
        </div>

        {/* Tab Buttons */}
        <div className="flex items-center gap-1 rounded-xl bg-slate-100/80 p-1">
          <button
            type="button"
            onClick={() => setChartMode('weekly')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
              chartMode === 'weekly' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <TrendingUp className="h-3.5 w-3.5" /> Weekly Trend
          </button>
          <button
            type="button"
            onClick={() => setChartMode('monthly')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
              chartMode === 'monthly' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Calendar className="h-3.5 w-3.5" /> Monthly Volume
          </button>
          <button
            type="button"
            onClick={() => setChartMode('reps')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
              chartMode === 'reps' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Users className="h-3.5 w-3.5" /> Rep Leaderboard
          </button>
          <button
            type="button"
            onClick={() => setChartMode('purpose')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
              chartMode === 'purpose' ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <PieIcon className="h-3.5 w-3.5" /> Purpose Split
          </button>
        </div>
      </div>

      {/* Analytics KPI Row */}
      <div className="grid grid-cols-2 gap-3 py-4 sm:grid-cols-4 border-b border-slate-100">
        <div className="rounded-xl bg-slate-50 p-3">
          <span className="text-[11px] font-semibold uppercase text-slate-400">Total Analyzed</span>
          <p className="mt-1 text-xl font-bold tabular-nums text-slate-900">{dataPool.length}</p>
        </div>
        <div className="rounded-xl bg-emerald-50/70 p-3">
          <span className="text-[11px] font-semibold uppercase text-emerald-600">Completion Rate</span>
          <p className="mt-1 text-xl font-bold tabular-nums text-emerald-700">{completionRate}%</p>
        </div>
        <div className="rounded-xl bg-blue-50/70 p-3">
          <span className="text-[11px] font-semibold uppercase text-blue-600">Completed Visits</span>
          <p className="mt-1 text-xl font-bold tabular-nums text-blue-700">{totalCompleted}</p>
        </div>
        <div className="rounded-xl bg-purple-50/70 p-3">
          <span className="text-[11px] font-semibold uppercase text-purple-600">Pipeline Conversions</span>
          <p className="mt-1 text-xl font-bold tabular-nums text-purple-700">{totalConversions}</p>
        </div>
      </div>

      {/* Chart Canvas Area */}
      <div className="pt-4">
        {/* MODE 1: WEEKLY TREND AREA CHART */}
        {chartMode === 'weekly' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-slate-600">Daily Visit Trajectory (Completed vs Planned vs In-Field)</p>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1 font-medium text-emerald-600">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Completed
                </span>
                <span className="flex items-center gap-1 font-medium text-blue-600">
                  <span className="h-2.5 w-2.5 rounded-full bg-blue-500" /> Checked In
                </span>
                <span className="flex items-center gap-1 font-medium text-amber-600">
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> Planned
                </span>
              </div>
            </div>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={weeklyTrendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="colorCheckedIn" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="colorPlanned" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#F59E0B" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748B' }} tickLine={false} axisLine={{ stroke: '#CBD5E1' }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748B' }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomChartTooltip />} />
                  <Area type="monotone" dataKey="completed" name="Completed" stroke="#10B981" strokeWidth={2.5} fillOpacity={1} fill="url(#colorCompleted)" />
                  <Area type="monotone" dataKey="checkedIn" name="Checked In" stroke="#3B82F6" strokeWidth={2} fillOpacity={1} fill="url(#colorCheckedIn)" />
                  <Area type="monotone" dataKey="planned" name="Planned" stroke="#F59E0B" strokeWidth={2} strokeDasharray="4 4" fillOpacity={1} fill="url(#colorPlanned)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* MODE 2: MONTHLY VOLUME BAR CHART */}
        {chartMode === 'monthly' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-slate-600">Monthly Visit Volume & Pipeline Conversions</p>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1 font-medium text-blue-600">
                  <span className="h-2.5 w-2.5 rounded-sm bg-blue-500" /> Total Visits
                </span>
                <span className="flex items-center gap-1 font-medium text-emerald-600">
                  <span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" /> Completed
                </span>
                <span className="flex items-center gap-1 font-medium text-purple-600">
                  <span className="h-2.5 w-2.5 rounded-sm bg-purple-500" /> Converted
                </span>
              </div>
            </div>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyTrendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} tickLine={false} axisLine={{ stroke: '#CBD5E1' }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748B' }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomChartTooltip />} />
                  <Bar dataKey="total" name="Total Scheduled" fill="#3B82F6" radius={[6, 6, 0, 0]} maxBarSize={36} />
                  <Bar dataKey="completed" name="Completed" fill="#10B981" radius={[6, 6, 0, 0]} maxBarSize={36} />
                  <Bar dataKey="converted" name="Converted (Lead/Followup)" fill="#8B5CF6" radius={[6, 6, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* MODE 3: REP LEADERBOARD BAR CHART */}
        {chartMode === 'reps' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-slate-600">Field Visits Completed & Planned by Representative</p>
            </div>
            {repChartData.length === 0 ? (
              <div className="flex h-56 items-center justify-center text-xs text-slate-400 font-medium">
                No representative activity recorded yet.
              </div>
            ) : (
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={repChartData} layout="vertical" margin={{ top: 5, right: 20, left: 40, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#64748B' }} tickLine={false} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#334155', fontWeight: 600 }} tickLine={false} axisLine={false} width={90} />
                    <Tooltip content={<CustomChartTooltip />} />
                    <Bar dataKey="completed" name="Completed" fill="#10B981" radius={[0, 6, 6, 0]} barSize={14} />
                    <Bar dataKey="checkedIn" name="Checked In" fill="#3B82F6" radius={[0, 6, 6, 0]} barSize={14} />
                    <Bar dataKey="planned" name="Planned" fill="#F59E0B" radius={[0, 6, 6, 0]} barSize={14} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* MODE 4: PURPOSE SPLIT PIE CHART */}
        {chartMode === 'purpose' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <div className="h-64 w-full flex items-center justify-center">
              {purposeChartData.length === 0 ? (
                <div className="text-xs text-slate-400 font-medium">No purpose categories recorded yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={purposeChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={85}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {purposeChartData.map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="space-y-2 max-h-56 overflow-y-auto pr-2">
              <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Purpose Breakdown</p>
              {purposeChartData.map((entry, idx) => {
                const color = PIE_COLORS[idx % PIE_COLORS.length];
                const total = purposeChartData.reduce((sum, item) => sum + item.value, 0);
                const percent = total > 0 ? Math.round((entry.value / total) * 100) : 0;
                return (
                  <div key={entry.name} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 border border-slate-100">
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
                      <span className="text-xs font-medium text-slate-800">{entry.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-900">{entry.value}</span>
                      <span className="text-[10px] font-semibold text-slate-400">({percent}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
